import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3, Settings, Save } from 'lucide-react';

const ServicesManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    service_type: 'عمرة',
    category: 'خدمات دينية',
    base_price: '',
    min_price: '',
    is_fixed_price: true,
    is_active: true
  });

  // Check if user can manage services
  const canManageServices = user?.role === 'super_admin' || user?.role === 'general_accountant' || user?.role === 'general_manager' || user?.role === 'services_manager';

  useEffect(() => {
    if (canManageServices) {
      fetchServices();
    }
  }, [canManageServices]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Sort services by creation date - newest first
      const sortedServices = response.data.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setServices(sortedServices);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        await axios.post(`${API}/services`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      setShowAddDialog(false);
      setEditingService(null);
      setFormData({
        name: '',
        description: '',
        service_type: 'عمرة',
        category: 'خدمات دينية',
        base_price: '',
        min_price: '',
        is_fixed_price: true,
        is_active: true
      });
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      alert('خطأ في حفظ الخدمة');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      service_type: service.service_type,
      category: service.category,
      base_price: service.base_price.toString(),
      min_price: service.min_price?.toString() || '',
      is_fixed_price: service.is_fixed_price,
      is_active: service.is_active
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الخدمة؟')) {
      try {
        await axios.delete(`${API}/services/${serviceId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('خطأ في حذف الخدمة');
      }
    }
  };

  if (!canManageServices) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-red-600">⚠️ ليس لديك صلاحية لإدارة الخدمات</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('servicesManagement')}</h1>
        <Button onClick={() => setShowAddDialog(true)} className="bg-brand-600 hover:bg-brand-700">
          <Plus className="h-4 w-4 ml-2" />
          {t('addService')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">{t('serviceName')}</TableHead>
                <TableHead className="text-right">{t('serviceType')}</TableHead>
                <TableHead className="text-right">{t('serviceCategory')}</TableHead>
                <TableHead className="text-right">{t('basePrice')}</TableHead>
                <TableHead className="text-right">{t('priceType')}</TableHead>
                <TableHead className="text-right">{t('status')}</TableHead>
                <TableHead className="text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell className="text-right font-medium">{service.name}</TableCell>
                  <TableCell className="text-right">{service.service_type}</TableCell>
                  <TableCell className="text-right">{service.category}</TableCell>
                  <TableCell className="text-right">{service.base_price.toLocaleString()} دج</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={service.is_fixed_price ? "default" : "outline"}>
                      {service.is_fixed_price ? '🔒 ثابت' : '🔄 متغير'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={service.is_active ? "default" : "secondary"}>
                      {service.is_active ? t('isActive') : 'غير نشطة'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(service)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(service.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Service Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[425px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>
              {editingService ? t('editService') : t('addService')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('serviceName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="service_type">{t('serviceType')}</Label>
              <Select value={formData.service_type} onValueChange={(value) => setFormData({...formData, service_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="عمرة">{t('umrah')}</SelectItem>
                  <SelectItem value="حج">{t('hajj')}</SelectItem>
                  <SelectItem value="تذكرة طيران">{t('flightTicket')}</SelectItem>
                  <SelectItem value="حجز فندق">{t('hotelBooking')}</SelectItem>
                  <SelectItem value="خدمة تأشيرة">{t('visaService')}</SelectItem>
                  <SelectItem value="نقل">{t('transport')}</SelectItem>
                  <SelectItem value="تأمين">{t('insurance')}</SelectItem>
                  <SelectItem value="خدمة جواز سفر">{t('passportService')}</SelectItem>
                  <SelectItem value="أخرى">{t('otherService')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="category">{t('serviceCategory')}</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="خدمات دينية">{t('religiousServices')}</SelectItem>
                  <SelectItem value="خدمات سفر">{t('travelServices')}</SelectItem>
                  <SelectItem value="خدمات وثائق">{t('documentationServices')}</SelectItem>
                  <SelectItem value="خدمات إقامة">{t('accommodationServices')}</SelectItem>
                  <SelectItem value="أخرى">{t('otherCategory')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="base_price">{t('basePrice')}</Label>
              <Input
                id="base_price"
                type="number"
                value={formData.base_price}
                onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                required
              />
            </div>

            <div>
              <Label htmlFor="min_price">{t('minPrice')}</Label>
              <Input
                id="min_price"
                type="number"
                value={formData.min_price}
                onChange={(e) => setFormData({...formData, min_price: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="price_type">{t('priceType')}</Label>
              <Select 
                value={formData.is_fixed_price ? 'fixed' : 'variable'} 
                onValueChange={(value) => setFormData({...formData, is_fixed_price: value === 'fixed'})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">🔒 {t('fixedPriceService')}</SelectItem>
                  <SelectItem value="variable">🔄 {t('variablePriceService')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-600 mt-1">
                {formData.is_fixed_price 
                  ? "🔒 موظفو الوكالة لا يستطيعون تغيير السعر" 
                  : "🔄 يمكن لموظفي الوكالة تعديل السعر في العمليات اليومية"
                }
              </p>
            </div>

            <div>
              <Label htmlFor="description">{t('serviceDescription')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
              <Label htmlFor="is_active">{t('isActive')}</Label>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit">
                {t('save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Agency Settings Management Component


export default ServicesManagement;
